// Bản sao danh mục phía trình duyệt (nguồn gốc: functions/src/domain.ts).
export const SITES: Record<string, string> = {
  AHG: "Toàn công ty", ADL: "Andalat", ATV: "An the Village", CH65: "Căn hộ 65", VP: "Văn phòng AHG", PTA: "Phòng tranh",
};
export const DOC_CATEGORIES: Record<string, string> = {
  PL: "01 Pháp lý", HD: "02 Hợp đồng", TC: "03 Tài chính – Kế toán", MKT: "04 Thương hiệu & Marketing",
  IMG: "05 Hình ảnh & Video", VH: "06 Vận hành", NS: "07 Nhân sự", TS: "08 Tài sản & Bảo trì",
};
export const SENSITIVE = ["HD", "TC", "NS"];
export const ROLE_LABEL: Record<string, string> = {
  admin: "Quản trị", bgd: "Ban giám đốc", gm: "Quản lý chung", site_mgr: "Quản lý cơ sở",
  staff: "Nhân viên cơ sở", marketing: "Marketing", accountant: "Kế toán", none: "Chưa cấp quyền",
};
export const STATUS_LABEL: Record<string, string> = {
  draft: "Nháp", pending_review: "Chờ duyệt", approved: "Đã duyệt", superseded: "Đã thay thế", changes_requested: "Cần sửa",
};
export const ROLE_DESC: Record<string, string> = {
  admin: "Toàn quyền, cấp quyền thành viên, xem nhật ký. Chỉ Quản trị xóa được tài liệu đã duyệt.",
  bgd: "Xem và duyệt tài liệu mọi cơ sở. Lịch pháp lý chỉ xem; không xóa tài liệu.",
  gm: "Quản lý chung: xem và duyệt tài liệu mọi cơ sở, sửa ngày hiệu lực, xóa bản chưa duyệt, xem nhật ký.",
  site_mgr: "Xem, tải lên, duyệt tài liệu của cơ sở được giao (Pháp lý, Hợp đồng do mình tải cần người khác duyệt); xóa bản chưa duyệt; xem giá trị tranh tại cơ sở.",
  staff: "Xem tài liệu đã duyệt của cơ sở (Pháp lý chỉ xem); không xem Hợp đồng, Tài chính, Nhân sự.",
  marketing: "Chỉ xem Hình ảnh & Video của mọi cơ sở.",
  accountant: "Xem tài liệu đã duyệt mọi cơ sở, tải lên, chia sẻ dạng chỉ xem; không duyệt, không xóa.",
};
/** Vai trò cần chọn cơ sở. */
export const SITE_SCOPED = ["site_mgr", "staff"];
export interface Member { email: string; name: string; role: string; sites: string[]; active: boolean; emailOff?: boolean }
export const isGlobal = (m: Member) => ["admin", "bgd", "gm", "accountant"].includes(m.role);
/** Phản chiếu canApprove ở máy chủ. */
export const canReview = (m: Member) => ["admin", "bgd", "gm"].includes(m.role) || (m.role === "site_mgr" && m.sites.length > 0);
export const canSeeAudit = (m: Member) => ["admin", "bgd", "gm"].includes(m.role);
export const removeDiacritics = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D");

// ---------- Phản chiếu quyền ở máy chủ (functions/src/domain.ts) để chỉ hiện nút đúng quyền.
// Máy chủ vẫn kiểm tra lại mọi thao tác; ẩn nút chỉ để người dùng không bấm nhầm.
const GLOBAL = ["admin", "bgd", "gm"];
export const scopedSites = (m: Member): string[] => (["site_mgr", "staff"].includes(m.role) ? m.sites : Object.keys(SITES).filter((s) => s !== "AHG"));
export function canUploadTo(m: Member, site: string, cat: string): boolean {
  if (GLOBAL.includes(m.role) || m.role === "accountant") return true;
  if (m.role === "marketing") return false;
  if (m.role === "staff" && SENSITIVE.includes(cat)) return false;
  return ["site_mgr", "staff"].includes(m.role) && m.sites.includes(site);
}
export const canUploadAny = (m: Member) => m.role !== "marketing" && (GLOBAL.includes(m.role) || m.role === "accountant" || m.sites.length > 0);
export function canApproveSite(m: Member, site: string): boolean {
  if (GLOBAL.includes(m.role)) return true;
  return m.role === "site_mgr" && m.sites.includes(site);
}
/** Ai thấy được bản chưa duyệt (chờ duyệt, bị trả lại). */
export const seesPending = (m: Member, site: string, createdBy?: string) =>
  createdBy === m.email || ["admin", "bgd", "gm", "accountant"].includes(m.role) || canApproveSite(m, site);
/** Quyền Tải về theo vai trò (anh Khang chốt 22/10): Marketing chỉ xem; Nhân viên không tải về hồ sơ Pháp lý. */
export function canDownload(m: Member, cat: string): boolean {
  if (m.role === "marketing") return false;
  if (m.role === "staff" && cat === "PL") return false;
  return true;
}

// ---------- Phân quyền chốt sau kiểm thử đợt 2 (Q1–Q13). Bản sao của functions/src/domain.ts — máy chủ vẫn kiểm tra lại.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DocLike = { siteId?: string; category?: string; latestVersion?: number; hasApproved?: boolean; [k: string]: any };
const approvedOnce = (d: DocLike) => d.hasApproved === true || (d.latestVersion ?? 0) > 0;
const GUARDED = ["PL", "HD", "TC", "NS"];
/** Q1, Q5: bản đã duyệt chỉ Quản trị xóa; Quản lý chung / Quản lý cơ sở xóa bản chưa duyệt; BGĐ không xóa. */
export function canTrash(m: Member, d: DocLike): boolean {
  if (m.role === "admin") return true;
  if (approvedOnce(d)) return false;
  return m.role === "gm" || (m.role === "site_mgr" && m.sites.includes(d.siteId ?? ""));
}
/** Q3: Thùng rác chỉ Quản trị, Quản lý chung, Quản lý cơ sở. */
export const canSeeTrash = (m: Member) => ["admin", "gm", "site_mgr"].includes(m.role);
/** Q2, Q5: sửa ngày / đánh dấu gia hạn: Quản trị, Quản lý chung, Quản lý cơ sở (cơ sở mình). BGĐ chỉ xem. */
export const canManageExpiry = (m: Member, d: DocLike) => m.role === "admin" || m.role === "gm" || (m.role === "site_mgr" && m.sites.includes(d.siteId ?? ""));
/** Q4: Kế toán được chia sẻ dạng xem; người duyệt được tài liệu cũng chia sẻ được. */
export const canShare = (m: Member, d: DocLike) => m.role === "accountant" || canApproveSite(m, d.siteId ?? "");
/** Q7: bật "Cho phép tải về file gốc" khi chia sẻ. */
export const canShareDownload = (m: Member, d: DocLike) => ["admin", "bgd", "gm"].includes(m.role)
  || (m.role === "site_mgr" && m.sites.includes(d.siteId ?? "") && !GUARDED.includes(d.category ?? ""));
/** Q12: Pháp lý, Hợp đồng, Tài chính, Nhân sự do chính mình tải lên cần người khác duyệt (trừ Quản trị). */
export const canSelfApprove = (m: Member, cat: string) => m.role === "admin" || !GUARDED.includes(cat);
/** Q13: Nhân viên không gửi Bản mới cho Pháp lý. */
export const canUploadNewVersion = (m: Member, d: DocLike) => !(m.role === "staff" && d.category === "PL") && canUploadTo(m, d.siteId ?? "", d.category ?? "");
